// Status pill — reusable visual primitive for session/event status.
//
// Maps a status string to a colored pill matching Anthropic Console's
// look. Used in:
//   - Session detail header (status of the current run)
//   - Session list rows (status column)
//   - Event rows (per-event status like "awaiting approval")
//
// Color tokens (semantic, live in styles.css):
//   --sb-status-idle       — neutral gray
//   --sb-status-running    — green
//   --sb-status-pending    — amber/orange (HITL waiting)
//   --sb-status-completed  — green
//   --sb-status-failed     — red

import type React from 'react';

import { sessionStatusLabel } from '../pages/format.js';

export type StatusKind =
  | 'idle'
  | 'initializing'
  | 'running'
  | 'awaiting_approval'
  | 'rejected'
  | 'completed'
  | 'failed'
  | 'terminated'
  | 'unknown';

function statusToKind(status: string): StatusKind {
  switch (status) {
    case 'idle':
    case 'initializing':
    case 'running':
    case 'completed':
    case 'failed':
    case 'terminated':
    case 'awaiting_approval':
    case 'rejected':
      return status;
    default:
      return 'unknown';
  }
}

export interface StatusPillProps {
  readonly status: string;
  readonly label?: string;
}

export function StatusPill(props: StatusPillProps): React.ReactElement {
  const kind = statusToKind(props.status);
  const label = props.label ?? sessionStatusLabel(kind);
  return (
    <span className={`status-pill status-pill--${kind}`} data-status={kind}>
      {label}
    </span>
  );
}
